import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getConcierges } from "../../api/concierges/concierges";
import { getAllDivisions } from "../../api/divisions/divisionsApi";
import { AccessGroup, Division } from "../../api/divisions/types";

const useConciergesData = (groupId: string, groupName: string) => {
       const isEnabled = !(groupName === "");
       return useQuery({
              queryKey: ["concierges", groupName],
              queryFn: () => getConcierges(groupId),
              staleTime: 1000 * 60 * 15, // 15 minutes
              gcTime: 1000 * 60 * 15, // 15 minutes
              enabled: isEnabled && groupId !== undefined,
       });
};

export const useConciergeData = (divCode?: string) => {
    const { data: divisions, isLoading: isDivCodesLoading } = useQuery({
        queryKey: ["allDivisions"],
        queryFn: () => getAllDivisions({ filter: `status=true` }),
        staleTime: 1000 * 60 * 15, // 15 minutes
        gcTime: 1000 * 60 * 15, // 15 minutes
    });

    const conciergeParams = useMemo(() => {
        if (!divCode || !divisions?.data) {
            return { groupName: "", groupId: "", hasValidData: false };
        }
        const division = divisions.data.find((div: Division) => div.divisionCode === divCode);
        // Filter accessGroups for RW and status true
        const accessGroup = division?.accessGroups?.find(
            (ag: AccessGroup) => ag.groupName.includes("RW") && ag.status === true
        );
        return {
            groupName: accessGroup?.groupName || "",
            groupId: accessGroup?.groupId || "",
            hasValidData: !!accessGroup
        };
    }, [divisions?.data, divCode]);

    const defaultDataQuery = useConciergesData(
        conciergeParams.groupId || "",
        conciergeParams.groupName
    );

    if (!divCode || !conciergeParams.hasValidData) {
        return { filteredData: [], isLoading: isDivCodesLoading };
    }

    return {
        filteredData: defaultDataQuery.data || [],
        isLoading: isDivCodesLoading || defaultDataQuery.isLoading,
    };
};
